import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardAction } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert"
import {
  Play,
  RotateCcw,
  Check,
  Loader2,
  AlertCircle,
  Clock,
  Layers,
  FileSearch,
  Link2,
  Percent,
  DollarSign,
  ArrowUpFromLine,
  Building,
  Maximize,
} from "lucide-react"
import type { PluExtractionRun, PluZone, PluExtractionStatus } from "@/types"
import type { PluExtractionUpdate } from "@/hooks/use-plu-extraction-channel"

const EXTRACTION_STEPS = [
  { key: "resolving_plu", label: "Résolution du PLU" },
  { key: "fetching_zones", label: "Récupération des zones GPU" },
  { key: "downloading_pdf", label: "Téléchargement du règlement PDF" },
  { key: "extracting_with_ai", label: "Extraction IA (Gemini)" },
  { key: "analyzing_completeness", label: "Analyse de complétude" },
] as const

function completenessBadgeClass(pct: number): string {
  if (pct >= 95)
    return "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-300"
  if (pct >= 80)
    return "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
  return "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
}

type PluExtractionCardProps = {
  status: PluExtractionStatus
  run: PluExtractionRun | null
  zones: PluZone[]
  cableUpdate: PluExtractionUpdate | null
  isInProgress: boolean
  effectiveStatus: string | null
  stepTimings: Record<string, number>
  onExtract: () => void
}

export function PluExtractionCard({
  status,
  run,
  zones,
  cableUpdate,
  isInProgress,
  effectiveStatus,
  stepTimings,
  onExtract,
}: PluExtractionCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Extraction PLU</CardTitle>
        <CardDescription>Pipeline d'extraction automatique du Plan Local d'Urbanisme</CardDescription>
        {!isInProgress && (
          <CardAction>
            <Button onClick={onExtract} variant={status === "none" && !run ? "default" : "outline"} size="sm">
              {status === "none" && !run ? (
                <>
                  <Play className="size-4" />
                  Lancer l'extraction
                </>
              ) : (
                <>
                  <RotateCcw className="size-4" />
                  Relancer
                </>
              )}
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        <ExtractionPanel
          status={status}
          run={run}
          zones={zones}
          cableUpdate={cableUpdate}
          effectiveStatus={effectiveStatus}
          isInProgress={isInProgress}
          stepTimings={stepTimings}
        />
      </CardContent>
    </Card>
  )
}

function ExtractionPanel({
  status,
  run,
  zones,
  cableUpdate,
  effectiveStatus,
  isInProgress,
  stepTimings,
}: Omit<PluExtractionCardProps, "onExtract">) {
  if (status === "none" && !run) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <div className="rounded-full bg-muted p-4">
          <Play className="size-8 text-muted-foreground" />
        </div>
        <p className="text-muted-foreground text-center text-sm">
          Aucune extraction PLU n'a été effectuée pour cette commune.
        </p>
        <p className="text-muted-foreground/70 text-center text-xs">
          Cliquez sur « Lancer l'extraction » pour démarrer le processus.
        </p>
      </div>
    )
  }

  if (status === "failed" && cableUpdate?.type !== "step_update") {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>L'extraction a échoué</AlertTitle>
          {run?.error_message && <AlertDescription>{run.error_message}</AlertDescription>}
        </Alert>
      </div>
    )
  }

  const showStats = status === "completed" && run

  return (
    <div className={showStats ? "grid gap-6 lg:grid-cols-[auto_1px_1fr]" : ""}>
      {showStats && <ExtractionStats run={run} zones={zones} />}
      {showStats && <div className="hidden bg-border lg:block" />}

      <div>
        {EXTRACTION_STEPS.map((step, idx) => {
          const stepStatus = getStepStatus(step.key, effectiveStatus, isInProgress)
          const timing = stepTimings[step.key]
          const isLast = idx === EXTRACTION_STEPS.length - 1

          return (
            <div key={step.key} className="relative flex gap-3 pb-6 last:pb-0">
              {!isLast && <div className="absolute left-[11px] top-6 h-full w-px bg-border" />}
              <div className="relative z-10 shrink-0">
                <StepIcon status={stepStatus} />
              </div>
              <div className="flex flex-1 items-center justify-between min-h-6">
                <span className={stepStatus === "pending" ? "text-muted-foreground" : "font-medium"}>{step.label}</span>
                {timing != null && (
                  <span className="text-muted-foreground text-xs">
                    <Clock className="mr-1 inline size-3" />
                    {formatDuration(timing)}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

type StepStatus = "completed" | "active" | "pending"
type ExtractionStepKey = (typeof EXTRACTION_STEPS)[number]["key"]

function getStepStatus(stepKey: ExtractionStepKey, currentStatus: string | null, isInProgress: boolean): StepStatus {
  const stepOrder: ExtractionStepKey[] = EXTRACTION_STEPS.map((s) => s.key)
  const currentIdx = currentStatus ? stepOrder.indexOf(currentStatus as ExtractionStepKey) : -1
  const stepIdx = stepOrder.indexOf(stepKey)

  if (currentStatus === "completed" || currentStatus === "failed") return "completed"
  if (!isInProgress) return stepIdx <= currentIdx ? "completed" : "pending"
  if (stepIdx < currentIdx) return "completed"
  if (stepIdx === currentIdx) return "active"
  return "pending"
}

function StepIcon({ status }: { status: StepStatus }) {
  if (status === "completed") {
    return (
      <div className="flex size-6 items-center justify-center rounded-full bg-green-600">
        <Check className="size-3.5 text-white" />
      </div>
    )
  }
  if (status === "active") {
    return (
      <div className="flex size-6 items-center justify-center rounded-full bg-blue-600">
        <Loader2 className="size-3.5 animate-spin text-white" />
      </div>
    )
  }
  return <div className="size-6 rounded-full border-2 border-muted bg-background" />
}

type StatItem = { label: string; value: string; icon: React.ComponentType<{ className?: string }>; pct?: number }

function ExtractionStats({ run, zones }: { run: PluExtractionRun; zones: PluZone[] }) {
  const total = zones.length
  const hauteur = zones.filter((z) => z.max_hauteur != null && z.max_hauteur_confidence === "high").length
  const etages = zones.filter((z) => z.max_etages != null && z.max_etages_confidence === "high").length
  const emprise = zones.filter((z) => z.max_emprise_au_sol != null && z.max_emprise_au_sol_confidence === "high").length

  const extractionStats: StatItem[] = []
  if (run.gpu_zone_count != null)
    extractionStats.push({ label: "Zones GPU", value: String(run.gpu_zone_count), icon: Layers })
  if (run.extracted_zone_count != null)
    extractionStats.push({ label: "Zones extraites", value: String(run.extracted_zone_count), icon: FileSearch })
  if (run.matched_zone_count != null)
    extractionStats.push({ label: "Zones correspondantes", value: String(run.matched_zone_count), icon: Link2 })
  if (run.estimated_cost_usd != null)
    extractionStats.push({ label: "Coût estimé", value: `$${run.estimated_cost_usd}`, icon: DollarSign })

  const gpuTotal = run.gpu_zone_count ?? total
  const completeCount = zones.filter(
    (z) =>
      z.max_hauteur != null &&
      z.max_hauteur_confidence === "high" &&
      z.max_emprise_au_sol != null &&
      z.max_emprise_au_sol_confidence === "high",
  ).length
  const completePct = gpuTotal > 0 ? Math.round((completeCount / gpuTotal) * 100) : 0

  const confidenceStats: StatItem[] = []
  confidenceStats.push({ label: "Complétude", value: `${completePct}%`, icon: Percent, pct: completePct })
  if (gpuTotal > 0) {
    const hPct = (hauteur / gpuTotal) * 100
    const ePct = (etages / gpuTotal) * 100
    const emPct = (emprise / gpuTotal) * 100
    confidenceStats.push(
      { label: "Hauteur max", value: `${hauteur}/${gpuTotal}`, icon: ArrowUpFromLine, pct: hPct },
      { label: "Étages max", value: `${etages}/${gpuTotal}`, icon: Building, pct: ePct },
      { label: "Emprise au sol", value: `${emprise}/${gpuTotal}`, icon: Maximize, pct: emPct },
    )
  }

  return (
    <div className="grid grid-cols-2 gap-4 lg:min-w-64">
      <div className="flex flex-col gap-2">
        {extractionStats.map((stat) => {
          const Icon = stat.icon
          return (
            <div
              key={stat.label}
              className="flex items-center gap-2 rounded-md border border-border bg-secondary/50 px-3 py-2 text-secondary-foreground"
            >
              <Icon className="size-4" />
              <span className="text-sm font-medium">{stat.label}</span>
              <span className="ml-auto text-base font-bold tabular-nums">{stat.value}</span>
            </div>
          )
        })}
      </div>

      <div className="flex flex-col gap-2">
        {confidenceStats.map((stat) => {
          const Icon = stat.icon
          return (
            <div
              key={stat.label}
              className={`flex items-center gap-2 rounded-md border px-3 py-2 ${completenessBadgeClass(stat.pct!)}`}
            >
              <Icon className="size-4" />
              <span className="text-sm font-medium">{stat.label}</span>
              <span className="ml-auto text-base font-bold tabular-nums">{stat.value}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m}m ${s}s`
}
